import { CheckIcon, ChevronDown, CopyIcon } from 'lucide-react';
import { useState } from 'react';

import { cn } from '@/lib/utils';

/**
 * A collapsible section with a toggle button
 */
export interface CollapsibleProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  titleClassName?: string;
  contentClassName?: string;
}

export const Collapsible = ({
  title,
  children,
  defaultOpen = false,
  titleClassName,
  contentClassName,
}: CollapsibleProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between text-sm"
      >
        <h3 className={cn('font-medium', titleClassName)}>{title}</h3>
        <ChevronDown
          className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && <div className={cn('pt-2', contentClassName)}>{children}</div>}
    </div>
  );
};

/**
 * A copyable text component with click-to-copy functionality
 */
export interface CopyableTextProps {
  text: string;
  maxWidth?: string;
  className?: string;
}

export const CopyableText = ({
  text,
  maxWidth = '180px',
  className,
}: CopyableTextProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      type="button"
      className={cn(
        'group text-[11px] font-mono truncate text-left hover:text-primary transition-colors max-w-1/2 flex items-center gap-1.5',
        className,
      )}
      onClick={handleCopy}
    >
      <span className="truncate">{text}</span>
      {copied ? (
        <CheckIcon className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
      ) : (
        <CopyIcon className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 text-muted-foreground flex-shrink-0" />
      )}
    </button>
  );
};

/**
 * A component to display a key-value pair in the inspector
 */
export interface InfoRowProps {
  label: string;
  value: string;
}

export const InfoRow = ({ label, value }: InfoRowProps) => {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[11px] text-muted-foreground font-medium">
        {label}
      </span>
      <CopyableText text={value} />
    </div>
  );
};
